import React, { useEffect, useState, useRef } from "react";
import { Alert, Spin, Select } from "antd";
import { useServices } from "../../context/ServiceProvider";
import ESMComponentWrapper from "../esm/ESMComponentWrapper";
import { PPC_TYPE_TO_ESM, derivePpcTypeFromRow } from "../../utils/esmConfig";
import {
  getPreviewOptionsForRecordType,
  hasMultiplePreviewOptions,
  getDefaultPreviewType,
} from "../../config/previewTypeConfig";
import { fromSalesforceProjectPageComponent } from "../../schema/projectPageComponentSchema";
import "./cg-styles.css";

// PreviewOrchestrator
// Renders previews for three object types:
// - "ProjectPage": renders the selected Project Page with its related components (if any) and a header slot (TBD)
// - "ProjectPageComponent": renders a single component standalone
// - "QualSteps": renders list of qualification steps in a placeholder layout

// Canonical Project Page types (kept for clarity; unused here but informative)
// Note: list retained for documentation purposes
// const PROJECT_PAGE_TYPES = [
//   "Project Splash Page (Preapply)",
//   "Project Pre-Qualification",
//   "Project Qualifying Page",
//   "Project Active Page",
//   "Default Qualification Page",
// ];

export default function PreviewOrchestrator({
  objectType,
  // one of: "ProjectPage" | "ProjectPageComponent" | "QualSteps"
  id,
  payload,
}) {
  const { projectPageComponent, projectQualStep } = useServices();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [pageComponents, setPageComponents] = useState([]);
  const [ppc, setPpc] = useState(null);
  const [qualSteps, setQualSteps] = useState([]);
  const [selectedPreviewType, setSelectedPreviewType] = useState(null);

  // Track previous record type to detect actual changes
  const prevRecordTypeRef = useRef(null);

  useEffect(() => {
    console.log("PreviewOrchestrator", objectType, payload);
  }, [objectType, payload]);

  // Helper to convert ESM component names to CE UI 'type' values
  // No longer needed for PPC-only preview rendering

  // Initialize selected preview type when record type actually changes
  useEffect(() => {
    let recordTypeName = objectType;
    if (objectType === "ProjectPageComponent" && ppc) {
      recordTypeName = ppc?.info?.recordTypeName;
    }

    // Only reset if record type actually changed (not just content fields)
    if (recordTypeName !== prevRecordTypeRef.current) {
      prevRecordTypeRef.current = recordTypeName;

      if (recordTypeName) {
        const defaultPreview = getDefaultPreviewType(recordTypeName);
        if (defaultPreview) {
          setSelectedPreviewType(defaultPreview);
        } else {
          setSelectedPreviewType(null);
        }
      }
    }
  }, [objectType, ppc?.info?.recordTypeName]);

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        if (objectType === "ProjectPage") {
          if (!id) throw new Error("ProjectPage preview needs an id");
          const comps =
            await projectPageComponent.fetchProjectPageComponentsByProjectPageId(
              id,
            );
          if (!ignore) setPageComponents(Array.isArray(comps) ? comps : []);
          // PPC-only preview; do not fetch Project Page record
        } else if (objectType === "ProjectPageComponent") {
          // If an override is provided, do not fetch

          if (!id) throw new Error("ProjectPageComponent preview needs an id");
          const record =
            await projectPageComponent.fetchProjectPageComponent(id);
          if (!ignore) setPpc(record);
        } else if (objectType === "QualSteps") {
          // Prefer objective scope, else fallback to project scope
          if (payload.projectObjectiveId) {
            const list =
              await projectQualStep.fetchQualStepsByProjectObjectiveId(
                payload.projectObjectiveId,
              );
            if (!ignore) setQualSteps(Array.isArray(list) ? list : []);
          } else if (payload.projectId) {
            const list = await projectQualStep.fetchQualStepsByProjectId(
              payload.projectId,
            );
            if (!ignore) setQualSteps(Array.isArray(list) ? list : []);
          } else {
            if (!ignore) setQualSteps([]);
          }
        }
      } catch (e) {
        if (!ignore) setError(e?.message || "Failed to load preview");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, [objectType, id, projectPageComponent, projectQualStep, payload]);

  // Layout mapping not needed for PPC-only preview rendering

  // Create a unique key based on objectType and critical payload properties to force re-render
  // This must be called before any early returns (React Hook rules)
  const payloadKey = React.useMemo(() => {
    if (!payload) return objectType || "";
    // Include projectContributorStatus in key to force re-render when switching between ProjectCard and ProjectCardInvite
    const projectStatus = payload?.project?.projectContributorStatus || "";
    const inviteDate = payload?.project?.invitedToProjectDate
      ? new Date(payload.project.invitedToProjectDate).getTime()
      : "";
    return `${objectType || ""}-${projectStatus}-${inviteDate}`;
  }, [objectType, payload]);

  if (loading) {
    return (
      <div className="p-6">
        <Spin />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <Alert type="error" showIcon message={error} />
      </div>
    );
  }

  // if (objectType === "ProjectPage") {
  //   // Sort components by order and render each using ESM component mapping
  //   const rowsOrdered = (Array.isArray(pageComponents) ? pageComponents : [])
  //     .slice()
  //     .sort((a, b) => {
  //       const ao = a.Component_Order__c ?? "";
  //       const bo = b.Component_Order__c ?? "";
  //       return String(ao).localeCompare(String(bo), undefined, {
  //         numeric: true,
  //         sensitivity: "base",
  //       });
  //     });

  //   return (
  //     <div className="space-y-4">
  //       <div className="bg-white border border-gray-200 rounded-lg p-4">
  //         <div className="text-lg font-semibold text-gray-900">
  //           Project Page
  //         </div>
  //         <div className="text-sm text-gray-600">ID: {id || "—"}</div>
  //       </div>
  //       {rowsOrdered.map((row) => {
  //         const rowType = derivePpcTypeFromRow(row);
  //         const ppc = fromSalesforceProjectPageComponent(row);
  //         const { esmName, props } = mapPpcToProps(ppc, rowType);

  //         return (
  //           <ESMComponentWrapper
  //             key={row.Id || row.id || rowType}
  //             componentName={esmName}
  //             noWrapper={true}
  //             {...props}
  //           />
  //         );
  //       })}
  //     </div>
  //   );
  // }

  // if (objectType === "ProjectPageComponent") {
  //   if (!ppc) return null;

  //   const recordTypeName = ppc?.info?.recordTypeName;
  //   const previewConfig = getPreviewOptionsForRecordType(recordTypeName);
  //   const showSelector = hasMultiplePreviewOptions(recordTypeName);

  //   // Determine which ESM component name to use
  //   let esmNameToUse;
  //   let propsToUse;

  //   if (showSelector && selectedPreviewType) {
  //     // Use the selected preview type from dropdown
  //     const result = mapPpcToProps(ppc, selectedPreviewType);
  //     esmNameToUse = result.esmName;
  //     propsToUse = result.props;
  //   } else if (previewConfig?.defaultPreview) {
  //     // Use the default preview type from config
  //     esmNameToUse = previewConfig.defaultPreview;
  //     propsToUse = mapPpcToProps(ppc, null).props;
  //   } else {
  //     // Fall back to record type mapping
  //     const explicitType = Object.keys(PPC_TYPE_TO_ESM).find(
  //       (t) =>
  //         String(t).toLowerCase() === String(recordTypeName || "").toLowerCase()
  //     );
  //     const mappedResult = mapPpcToProps(ppc, explicitType);
  //     esmNameToUse = mappedResult.esmName;
  //     propsToUse = mappedResult.props;
  //   }

  //   return (
  //     <div className="space-y-4">
  //       {showSelector && previewConfig?.options && (
  //         <div className="bg-white border border-gray-200 rounded-lg p-3">
  //           <div className="flex items-center gap-3">
  //             <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
  //               Preview As:
  //             </label>
  //             <Select
  //               value={selectedPreviewType || previewConfig.defaultPreview}
  //               onChange={(value) => setSelectedPreviewType(value)}
  //               options={previewConfig.options}
  //               className="flex-1"
  //               placeholder="Select preview type"
  //             />
  //           </div>
  //         </div>
  //       )}
  //       <ESMComponentWrapper
  //         key={selectedPreviewType || esmNameToUse}
  //         componentName={esmNameToUse}
  //         noWrapper={true}
  //         {...propsToUse}
  //       />
  //     </div>
  //   );
  // }

  // Project Qual Steps placeholder page
  if (objectType === "QualSteps") {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-lg font-semibold text-gray-900">
            Qualification Steps
          </div>
          <div className="text-sm text-gray-600">
            {payload.projectObjectiveId
              ? `Objective: ${payload.projectObjectiveId}`
              : payload.projectId
                ? `Project: ${payload.projectId}`
                : "—"}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          {qualSteps.length === 0 ? (
            <div className="text-sm text-gray-500">No qualification steps.</div>
          ) : (
            <ul className="space-y-2">
              {qualSteps.map((s) => (
                <li
                  key={s.Id || s.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded border text-sm"
                >
                  <div className="space-y-0.5">
                    <div className="font-medium text-gray-900">
                      {s.Name || s.name}
                    </div>
                    <div className="text-gray-600">
                      Funnel: {s.Funnel__c || s.funnel || "—"} • Status:{" "}
                      {s.Project_Qualification_Step_Status__c ||
                        s.status ||
                        "—"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // Key includes image URLs so preview remounts when presigned URLs arrive and images display
  const previewKey =
    objectType && payload
      ? `${objectType}-${(payload.leftMediaUrl || "").slice(0, 80)}-${(payload.rightMediaUrl || "").slice(0, 80)}-${(payload.mediaUrl || "").slice(0, 80)}-${(payload.middleMediaUrl || "").slice(0, 80)}`
      : objectType;

  return (
    (objectType && payload && (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <ESMComponentWrapper
          key={previewKey}
          componentName={objectType}
          {...payload}
        />
      </div>
    )) || (
      <div className="p-6">
        <Alert type="warning" message="Unsupported preview type" />
      </div>
    )
  );
}
